<template>
    <section>
        <titlebar :title-stack="['Visão geral']">
            <template #right>
                <b-button
                    v-if="isInRole('prd-orcamentos-alterar')"
                    class="prd-new-budget-button"
                    type="is-primary"
                    icon-left="file-document-plus-outline"
                    rounded
                    tag="router-link"
                    :to="{ name: 'prd-orcamento-novo' }"
                    >Novo orçamento</b-button
                >
            </template>
        </titlebar>
        <div class="section prd-page">
            <b-loading :active="loading" :is-full-page="false" />

            <h2 v-if="podeConsultarOrcamentos" class="prd-section-title">Orçamentos</h2>
            <div v-if="podeConsultarOrcamentos" class="prd-metric-grid">
                <router-link
                    v-for="card in cardsOrcamento"
                    :key="card.status"
                    class="prd-metric-card"
                    :to="{ name: 'prd-orcamentos', query: { status: card.status } }"
                >
                    <span class="prd-metric-icon" :class="card.color"><b-icon :icon="card.icon" /></span>
                    <div>
                        <strong>{{ valor(dados.orcamentos, card.key) }}</strong
                        ><span>{{ card.label }}</span>
                    </div>
                    <b-icon icon="chevron-right" class="prd-chevron" />
                </router-link>
            </div>

            <h2 v-if="cardsProducaoVisiveis.length" class="prd-section-title">Produção</h2>
            <div v-if="cardsProducaoVisiveis.length" class="prd-metric-grid prd-orders">
                <router-link
                    v-for="card in cardsProducaoVisiveis"
                    :key="card.status"
                    class="prd-metric-card"
                    :to="card.status === 7 ? { name: 'prd-analise' } : { name: 'prd-producao', query: { situacao: card.situacao } }"
                >
                    <span class="prd-metric-icon" :class="card.color"><b-icon :icon="card.icon" /></span>
                    <div>
                        <strong>{{ valor(dados.producao, card.key) }}</strong
                        ><span>{{ card.label }}</span>
                    </div>
                    <b-icon icon="chevron-right" class="prd-chevron" />
                </router-link>
            </div>

            <div class="columns mt-5">
                <div class="column is-7">
                    <div class="prd-panel">
                        <div class="prd-panel-header">
                            <div>
                                <h2>Atividades recentes</h2>
                                <p>Últimas movimentações do módulo</p>
                            </div>
                            <b-icon icon="history" />
                        </div>
                        <prd-empty-state
                            v-if="!dados.atividades || !dados.atividades.length"
                            icon="history"
                            title="Sem atividades recentes"
                            description="As movimentações aparecerão aqui."
                        />
                        <div v-else class="prd-timeline">
                            <div v-for="item in dados.atividades" :key="item.id" class="prd-timeline-item">
                                <span class="prd-timeline-dot" />
                                <div class="prd-timeline-content">
                                    <div class="prd-timeline-title">
                                        <strong>{{ limparAcao(item.acao) }}</strong>
                                        <small>{{ data(item.dataHora) }}</small>
                                    </div>
                                    <p v-if="item.usuarioNome" class="prd-timeline-user">por {{ item.usuarioNome }}</p>
                                    <p v-if="item.descricao" class="prd-timeline-description">{{ item.descricao }}</p>
                                    <p v-if="item.valorAnterior || item.valorInformado" class="prd-timeline-values">
                                        <span v-if="item.valorAnterior"><strong>De:</strong> {{ item.valorAnterior }}</span>
                                        <span v-if="item.valorInformado"><strong>Para:</strong> {{ item.valorInformado }}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="column is-5">
                    <div class="prd-panel">
                        <div class="prd-panel-header">
                            <div>
                                <h2>Acesso rápido</h2>
                                <p>Continue de onde parou</p>
                            </div>
                            <b-icon icon="lightning-bolt-outline" />
                        </div>
                        <div class="prd-shortcuts">
                            <router-link v-if="podeConsultarOrcamentos" :to="{ name: 'prd-orcamentos' }"
                                ><b-icon icon="file-document-multiple-outline" /><span
                                    ><strong>Orçamentos</strong><small>Consultar e acompanhar</small></span
                                ></router-link
                            >
                            <router-link v-if="podeAcessarAnalise" :to="{ name: 'prd-analise' }"
                                ><b-icon icon="clipboard-search-outline" /><span><strong>Análise</strong><small>Viabilidade e valores comerciais</small></span></router-link
                            >
                            <router-link v-if="podeConsultarProducao" :to="{ name: 'prd-producao' }"
                                ><b-icon icon="factory" /><span><strong>Produção</strong><small>Fabricação e entregas</small></span></router-link
                            >
                            <a v-if="isInRole('prd-notificacoes-consultar')" @click="abrirNotificacoes"
                                ><b-icon icon="bell-outline" /><span><strong>Notificações</strong><small>Ver pendências e avisos</small></span></a
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <prd-notificacoes-modal
            :active.sync="mostrarNotificacoes"
            :unidade-id="unidadeId"
            @total-atualizado="atualizarTotalNotificacoes"
        />
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import titlebar from '@/components/titlebar.vue'
import prdEmptyState from '@/components/prd/prdEmptyState.vue'
import prdNotificacoesModal from '@/components/prd/prdNotificacoesModal.vue'

export default {
    name: 'PrdDashboard',
    components: { titlebar, prdEmptyState, prdNotificacoesModal },
    data() {
        return {
            loading: false,
            mostrarNotificacoes: false,
            dados: { orcamentos: {}, producao: {}, atividades: [], notificacoesNaoLidas: 0 },
            cardsOrcamento: [
                { status: 1, key: 'Rascunho', label: 'Em Aberto', icon: 'refresh', color: 'blue' },
                { status: 2, key: 'Enviado', label: 'Enviado', icon: 'send-check-outline', color: 'teal' },
                { status: 5, key: 'SolicitacaoAlteracao', label: 'Solicitação de Alterações', icon: 'file-edit-outline', color: 'amber' },
                { status: 4, key: 'Recusado', label: 'Recusado', icon: 'close-circle-outline', color: 'violet' },
            ],
            cardsProducao: [
                { status: 7, key: 'AnaliseProducao', label: 'Análise Produção', icon: 'clipboard-search-outline', color: 'orange' },
                { status: 9, key: 'AprovadoProducao', label: 'Aprovado Produção', icon: 'factory', color: 'green', situacao: 1 },
                { status: 'entregues', key: 'Entregues', label: 'Entregues', icon: 'truck-check-outline', color: 'teal', situacao: 2 },
            ],
        }
    },
    computed: {
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
        podeConsultarOrcamentos() { return this.isInRole('prd-orcamentos-consultar') || this.isInRole('prd-orcamentos-alterar') },
        podeConsultarProducao() { return this.isInRole('prd-pedidos-consultar') || this.isInRole('prd-pedidos-produzir') || this.isInRole('prd-pedidos-entregar') },
        podeAcessarAnalise() { return this.isInRole('prd-analise-consultar') || this.isInRole('prd-analise-alterar') },
        cardsProducaoVisiveis() { return this.podeConsultarProducao ? this.cardsProducao.filter((card) => card.status !== 7 || this.podeAcessarAnalise) : [] },
    },
    watch: {
        unidadeId() {
            this.carregar()
        },
    },
    created() {
        this.carregar()
    },
    methods: {
        async carregar() {
            if (!this.unidadeId) return
            this.loading = true
            try {
                const res = await http.get(`/api/prd/dashboard?unidadeId=${this.unidadeId}`)
                this.dados = res.data
            } catch (e) {
                this.erro(e)
            } finally {
                this.loading = false
            }
        },
        valor(grupo, chave) {
            return grupo && grupo[chave] ? grupo[chave] : 0
        },
        data(v) {
            return v ? new Date(v).toLocaleString('pt-BR') : ''
        },
        limparAcao(v) {
            return (v || '').replace(/^Prd/, '').replace(/([a-z])([A-Z])/g, '$1 $2')
        },
        abrirNotificacoes() {
            this.mostrarNotificacoes = true
        },
        atualizarTotalNotificacoes(total) {
            this.dados.notificacoesNaoLidas = total
            this.$store.commit('componentes/SET_TOTAL_NOTIFICACAO', total)
        },
        erro(e) {
            this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível carregar o UsiFlow.', type: 'is-danger' })
        },
    },
}
</script>

<style scoped>
.prd-new-budget-button.button {
    min-height: 2.65rem;
    padding: 0.65rem 1.2rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, var(--brand-teal-500), var(--brand-teal-600));
    box-shadow: 0 7px 16px rgba(22, 143, 138, 0.24);
    font-weight: 700;
    letter-spacing: 0.01em;
    transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
}
.prd-new-budget-button.button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(22, 143, 138, 0.3);
    filter: brightness(1.05);
}
.prd-new-budget-button.button:focus-visible {
    box-shadow: 0 0 0 3px rgba(22, 143, 138, 0.18), 0 8px 18px rgba(22, 143, 138, 0.26);
}
.prd-new-budget-button.button:active {
    transform: translateY(0);
}
.prd-page {
    position: relative;
    background: var(--surface-page);
    min-height: calc(100vh - 110px);
}
.prd-hero {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(125deg, var(--brand-navy-900), var(--brand-navy-700));
    color: white;
    border-radius: 16px;
    padding: 1.5rem 1.75rem;
    box-shadow: 0 12px 30px rgba(20, 61, 104, 0.18);
}
.prd-hero .title,
.prd-hero p {
    color: white;
    margin-bottom: 0.25rem;
}
.eyebrow {
    letter-spacing: 0.16em;
    font-size: 0.7rem;
    font-weight: 700;
    opacity: 0.75;
}
.prd-notification-pill {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    background: rgba(255, 255, 255, 0.14);
    padding: 0.75rem 1rem;
    border-radius: 999px;
    cursor: pointer;
}
.prd-section-title {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7b8794;
    font-weight: 700;
    margin: 1.6rem 0 0.65rem;
}
.prd-metric-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.85rem;
}
.prd-metric-card {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    padding: 1rem;
    color: #273444;
    transition: 0.18s ease;
}
.prd-metric-card:hover {
    transform: translateY(-2px);
    border-color: #b9cbe2;
    box-shadow: 0 8px 22px rgba(31, 53, 78, 0.08);
}
.prd-metric-card strong {
    display: block;
    font-size: 1.55rem;
    line-height: 1.2;
}
.prd-metric-card span {
    font-size: 0.8rem;
    color: #66788a;
}
.prd-metric-icon {
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border-radius: 11px;
}
.prd-metric-icon.blue {
    background: #edf5ff;
    color: #2873c6;
}
.prd-metric-icon.amber {
    background: #fff6df;
    color: #b7791f;
}
.prd-metric-icon.violet {
    background: #f2efff;
    color: #6b52bb;
}
.prd-metric-icon.teal {
    background: #e7f8f3;
    color: #24866b;
}
.prd-metric-icon.orange {
    background: #fff1e8;
    color: #f97316;
}
.prd-metric-icon.green {
    background: #eaf8ef;
    color: #2f9e44;
}
.prd-metric-icon.purple {
    background: #f2edff;
    color: #7c3aed;
}
.prd-chevron {
    margin-left: auto;
    color: #a3afbd;
}
.prd-panel {
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    overflow: hidden;
    height: 100%;
}
.prd-panel-header {
    display: flex;
    justify-content: space-between;
    padding: 1rem 1.15rem;
    border-bottom: 1px solid #eef1f5;
}
.prd-panel-header h2 {
    font-weight: 700;
}
.prd-panel-header p {
    font-size: 0.78rem;
    color: #8492a6;
}
.prd-timeline {
    padding: 1rem 1.25rem 0.25rem 2rem;
}
.prd-timeline-item {
    position: relative;
    border-left: 2px solid #e7edf3;
    padding: 0 0 1.15rem 1.25rem;
}
.prd-timeline-item:last-child {
    border-left-color: transparent;
}
.prd-timeline-dot {
    position: absolute;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #3273dc;
    left: -6px;
    top: 0.35rem;
    box-shadow: 0 0 0 4px #eef5ff;
}
.prd-timeline-content {
    background: #f8fafc;
    border-radius: 10px;
    padding: 0.75rem 1rem;
}
.prd-timeline-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}
.prd-timeline-title small,
.prd-timeline-user {
    color: #8492a6;
}
.prd-timeline-title small {
    flex: none;
}
.prd-timeline-user,
.prd-timeline-values {
    font-size: 0.75rem;
}
.prd-timeline-description {
    margin-top: 0.35rem;
    color: #526275;
    white-space: pre-line;
}
.prd-timeline-values {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    margin-top: 0.35rem;
    color: #526275;
}
.prd-timeline-values strong {
    color: #334e68;
}
.prd-shortcuts {
    padding: 0.65rem;
}
.prd-shortcuts a {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.85rem;
    border-radius: 10px;
    color: #334e68;
}
.prd-shortcuts a:hover {
    background: #f4f8fc;
}
.prd-shortcuts small {
    display: block;
    color: #8492a6;
}
@media (max-width: 1024px) {
    .prd-metric-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}
@media (max-width: 600px) {
    .prd-page {
        padding: 1rem;
    }
    .prd-hero {
        align-items: flex-start;
        gap: 1rem;
    }
    .prd-notification-pill span {
        display: none;
    }
    .prd-metric-grid {
        grid-template-columns: 1fr;
    }
    .prd-timeline {
        padding-left: 1.5rem;
    }
    .prd-timeline-title {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.15rem;
    }
}
</style>
